"use client";

import { useInfront } from "@/context/infront-context";
import { DEFAULT_CONTENT } from "@/services/infront/utils";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";

type CompanyFundamentalsProps = {
  instrument: {
    feed: number;
    ticker: string;
  };
  actualLimit?: number;
};

const ID = "company-fundamentals";

const CompanyFundamentals = (props: CompanyFundamentalsProps) => {
  const { isReady, load } = useInfront();

  const [hasContent, setHasContent] = useState(false);
  const widgetRef = useRef<any>(null);

  useEffect(() => {
    let active = true;

    load?.(["core", "highstock", "hollowcandlestick", "heikinashi"]).then(
      () => {
        if (!isReady || !active) return;

        widgetRef.current?.destroy();
        widgetRef.current = null;

        const widgetOptions =
          new window.Infront.CompanyFundamentalsWidgetOptions();

        widgetOptions.actualLimit = props.actualLimit || 4;

        widgetOptions.hasContentCallback = (_hasContent: boolean) =>
          setHasContent(_hasContent);

        widgetOptions.instrument = new window.Infront.Instrument(
          props.instrument.feed,
          props.instrument.ticker
        );

        widgetOptions.defaultContent = DEFAULT_CONTENT;

        widgetOptions.rows = [
          window.Infront.CompanyFundamentalsRows.PSales,
          window.Infront.CompanyFundamentalsRows.EV_Sales,
          window.Infront.CompanyFundamentalsRows.PE_Adjusted,
          window.Infront.CompanyFundamentalsRows.ROE,
          window.Infront.CompanyFundamentalsRows.Dividend_yield,
          window.Infront.CompanyFundamentalsRows.Change_y,
          window.Infront.CompanyFundamentalsRows.Credit_losses,
          window.Infront.CompanyFundamentalsRows.PE,
          window.Infront.CompanyFundamentalsRows.Revenue,
          window.Infront.CompanyFundamentalsRows.Net_commission_income,
          window.Infront.CompanyFundamentalsRows.Net_interest_income,
          window.Infront.CompanyFundamentalsRows.Profit_before_tax,
          window.Infront.CompanyFundamentalsRows.Profit_after_tax,
          window.Infront.CompanyFundamentalsRows.Total_income,
          window.Infront.CompanyFundamentalsRows.FINANS_NETTO,
          window.Infront.CompanyFundamentalsRows.Eget_kapital,
          window.Infront.CompanyFundamentalsRows.Netto_resultat,
          window.Infront.CompanyFundamentalsRows.Total_costs,
          window.Infront.CompanyFundamentalsRows.Total_assets,
          window.Infront.CompanyFundamentalsRows.Stocks,
          window.Infront.CompanyFundamentalsRows.Dividend_per_share,
          window.Infront.CompanyFundamentalsRows.EPS,
          window.Infront.CompanyFundamentalsRows.EPS_adjusted,
          window.Infront.CompanyFundamentalsRows.EarningsYield,
          window.Infront.CompanyFundamentalsRows.EarningsYieldAdjusted,
          window.Infront.CompanyFundamentalsRows.EBIT,
          window.Infront.CompanyFundamentalsRows.EBITA,
          window.Infront.CompanyFundamentalsRows.EBITDA,
        ];

        widgetRef.current = window.infront.companyFundamentals(
          `#${ID}`,
          widgetOptions
        );
      }
    );

    return () => {
      active = false;
      widgetRef.current?.destroy();
      widgetRef.current = null;
    };
  }, [isReady, props.instrument]);

  return (
    <div
      className={classNames("card", {
        hidden: !hasContent,
      })}
      id={ID}
    />
  );
};

export default CompanyFundamentals;
